import { parseDurationToMinutes, formatDuration } from './timeCalculations';
import { CALENDAR_CONSTANTS } from './constants';

/**
 * Get duration in minutes for a service
 * @param {Object} service - Service object with calendarDuration
 * @returns {number} Duration in minutes
 */
export const getServiceDuration = (service) => {
    if (!service) return CALENDAR_CONSTANTS.MIN_DURATION_MINUTES;

    if (service.calendarDuration) {
        return parseDurationToMinutes(service.calendarDuration);
    }

    // Fallback to default duration
    return 60;
};

/**
 * Get all unique durations from services
 * @param {Array} services - Array of service objects
 * @returns {Array} Sorted array of unique durations in minutes
 */
export const getUniqueDurations = (services) => {
    const durations = new Set();

    services.forEach(service => {
        const duration = getServiceDuration(service);
        durations.add(duration);
    });

    return Array.from(durations).sort((a, b) => a - b);
};

/**
 * Create duration options for a select dropdown
 * @param {Array} services - Array of available services
 * @param {boolean} includeCustom - Whether to include custom duration option
 * @returns {Array} Array of {value, label} objects
 */
export const createDurationOptions = (services, includeCustom = true) => {
    const uniqueDurations = getUniqueDurations(services);

    const options = uniqueDurations.map(minutes => ({
        value: minutes,
        label: formatDuration(minutes),
    }));

    // Add common durations if not present
    const commonDurations = [30, 45, 60, 90, 120];
    commonDurations.forEach(minutes => {
        if (!uniqueDurations.includes(minutes)) {
            options.push({
                value: minutes,
                label: formatDuration(minutes),
            });
        }
    });

    // Sort by duration value
    options.sort((a, b) => a.value - b.value);

    // Add custom option if requested
    if (includeCustom) {
        options.push({
            value: 'custom',
            label: 'Własny czas...',
        });
    }

    return options;
};

/**
 * Validate if a duration is acceptable for a service
 * @param {number} durationMinutes
 * @param {Object} service
 * @returns {boolean}
 */
export const isValidDurationForService = (durationMinutes, service) => {
    // Check against minimum duration
    if (durationMinutes < CALENDAR_CONSTANTS.MIN_DURATION_MINUTES) {
        return false;
    }

    // Check against maximum duration
    if (durationMinutes > CALENDAR_CONSTANTS.MAX_DURATION_HOURS * 60) {
        return false;
    }

    // If service has specific duration, check if it matches
    if (service && service.calendarDuration) {
        const serviceDuration = getServiceDuration(service);

        // Allow some flexibility (±15 minutes) for services with set durations
        return Math.abs(durationMinutes - serviceDuration) <= 15;
    }

    return true;
};

/**
 * Calculate end time based on start time and service
 * @param {dayjs.Dayjs} startTime
 * @param {Object} service
 * @returns {dayjs.Dayjs} Calculated end time
 */
export const calculateEndTime = (startTime, service) => {
    const durationMinutes = getServiceDuration(service);
    return startTime.add(durationMinutes, 'minute');
};

/**
 * Get suggested durations based on time of day
 * @param {dayjs.Dayjs} startTime
 * @returns {Array} Suggested durations in minutes
 */
export const getSuggestedDurations = (startTime) => {
    const hour = startTime.hour();

    // Morning sessions - suggest shorter durations
    if (hour < 12) {
        return [30, 45, 60];
    }

    // Afternoon sessions - standard durations
    if (hour < 17) {
        return [45, 60, 90];
    }

    // Evening sessions - can be longer
    return [60, 90, 120];
};

/**
 * Format service info for display
 * @param {Object} service
 * @returns {Object} Formatted service info
 */
export const formatServiceInfo = (service) => {
    if (!service) {
        return {
            name: 'Brak usługi',
            duration: 'Nieokreślony czas',
            price: '-',
        };
    }

    const duration = getServiceDuration(service);

    return {
        name: service.serviceName || 'Bez nazwy',
        duration: formatDuration(duration),
        price: service.price ? `${service.price} zł` : 'Brak ceny',
        durationMinutes: duration,
    };
};

/**
 * Group services by duration
 * @param {Array} services
 * @returns {Object} Services grouped by duration
 */
export const groupServicesByDuration = (services) => {
    const grouped = {};

    services.forEach(service => {
        const duration = getServiceDuration(service);
        const key = formatDuration(duration);

        if (!grouped[key]) {
            grouped[key] = {
                duration,
                formattedDuration: key,
                services: [],
            };
        }

        grouped[key].services.push(service);
    });

    // Sort groups by duration
    const sortedGroups = Object.values(grouped).sort((a, b) => a.duration - b.duration);

    return sortedGroups;
};

/**
 * Estimate training session end time including buffer
 * @param {dayjs.Dayjs} startTime
 * @param {Object} service
 * @param {number} bufferMinutes - Buffer time between sessions
 * @returns {Object} {endTime, totalDuration}
 */
export const estimateSessionEnd = (startTime, service, bufferMinutes = 0) => {
    const serviceDuration = getServiceDuration(service);
    const totalDuration = serviceDuration + bufferMinutes;
    const endTime = startTime.add(totalDuration, 'minute');

    return {
        endTime,
        serviceDuration,
        bufferMinutes,
        totalDuration,
    };
};